import { useCanvasElementById } from "frontend/subscriptions";
import { SyncService } from "frontend/services/syncService";
import { Point } from "frontend/utils/math-utils";
import Konva from "konva";
import { KonvaEventObject } from "konva/types/Node";
import React, { useRef, useState } from "react";
import { Group } from "react-konva";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { CanvasElement } from "shared/datamodel/schemas/canvas-element";
import { useSubscribe } from "replicache-react";
import { getElement } from "shared/datamodel/canvas-element";
import { normalizedPointToCanvasPoint } from "frontend/utils/node-utils";
import { getElementTypeForId } from "../elements/canvas-elements-utils";

export default function OverlayElement({
  syncService,
  uniqueId,
  listening,
  onDragStarted,
  onDrag,
  onDragEnded,
  onClick,
  stageScale,
  renderElementComponent,
}: {
  syncService: SyncService<RW> | null;
  uniqueId: string;
  listening: boolean;
  onDragStarted: (node: Konva.Node) => void;
  onDrag: (node: Konva.Node, newPosition?: Point) => void;
  onDragEnded: (node: Konva.Node) => void;
  onClick: (e: KonvaEventObject<MouseEvent>) => void;
  stageScale: number;
  renderElementComponent: (uniqueId: string, element: CanvasElement) => JSX.Element;
}) {
  const element = useCanvasElementById(syncService?.getReplicache(), uniqueId);
  const ref = useRef<any>();

  const attachTarget = useSubscribe(
    syncService?.getReplicache(),
    async (tx) => {
      const target = (element as any)?.attachedTo;
      if (target) {
        return await getElement(tx, target);
      }
      return null;
    },
    null,
    [(element as any)?.attachedTo]
  );

  const [undoProperties, setUndoProperties] = useState<any>(null);

  function saveUndoProps() {
    const node = ref.current;
    if (node) {
      setUndoProperties(node.getPosition());
    }
  }

  function onDragStartInternal(e: any) {
    onDragStarted(e.target);
    saveUndoProps();
  }

  function onDragMove(e: any) {
    const node = e.target;
    onDrag(node);
  }

  function onDragEnd(e: any) {
    onDragEnded(e.target);
    setUndoProperties(null);
  }

  if (!element || element.hidden) {
    return null;
  }

  const isDragging = undoProperties != null;
  let x, y;
  if (isDragging) {
    x = undefined;
    y = undefined;
  } else if ((element as any).attachedTo && attachTarget) {
    const p = normalizedPointToCanvasPoint(
      (element as any).attachedLocation,
      getElementTypeForId((element as any).attachedTo),
      attachTarget
    );
    x = p.x;
    y = p.y;
  } else {
    x = element.x;
    y = element.y;
  }
  if (x && y && (x != element.x || y != element.y)) {
    // if we are attached to another shape, and it is moving, we'll update the element's x,y
    // with the computed values, so the thread-view will always open in the right place
    // TODO: perhaps CordThreadView should also compute the derived'd position from the attached shape
    onDrag(ref.current, { x, y });
  }

  return (
    <Group
      ref={ref}
      id={uniqueId}
      name={uniqueId}
      type="marker"
      x={x}
      y={y}
      scaleX={1 / stageScale}
      scaleY={1 / stageScale}
      draggable={true}
      listening={listening}
      onDragStart={onDragStartInternal}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      undoProperties={undoProperties}
      isCanvasElement={true}
      isConnectable={false}
      isConnector={false}
      saveUndoProps={saveUndoProps}
      element={element}
      onClick={onClick}
    >
      {renderElementComponent(uniqueId, element)}
    </Group>
  );
}
